import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Facebook,
  Instagram,
  Tiktok,
  Youtube,
  Megaphone,
} from "react-bootstrap-icons";

// LOGOS
import UntisLogo from "../../assets/icons/untis.png";
import NextcloudLogo from "../../assets/icons/nextcloud.png";
import MoodleLogo from "../../assets/icons/moodle.png";
import CiscoLogo from "../../assets/icons/cisco.png";
import MicrosoftLogo from "../../assets/icons/microsoft.png";

// Importiere die Ankündigungsdaten aus der JSON-Datei
import announcementsData from "../../data/announcements.json";
import AnnouncementModal from "./AnnouncementModal";

import "./Landing.css";

const LandingPage = () => {
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);

  // Datum im Format YYYY-MM-DD zur Prüfung
  const todayStr = new Date().toISOString().split("T")[0];

  // Sucht den ersten aktuell gültigen Eintrag aus dem JSON-Array
  const activeBadge = announcementsData.find(
    (item) => todayStr >= item.startDate && todayStr <= item.endDate,
  );

  return (
    <>
      <div className="social-service-bar py-2">
        <Container fluid className="px-lg-5">
          <div className="d-flex flex-wrap justify-content-center justify-content-md-start gap-3 align-items-center">
            <nav
              className="d-flex gap-3 pe-3 border-end border-secondary"
              aria-label="Social Media Kanäle"
            >
              <a
                href="https://www.facebook.com/bbs2leer"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-social-link facebook-hover"
                aria-label="BBS II Leer auf Facebook"
              >
                <Facebook aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/bbs2leer"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-social-link instagram-hover"
                aria-label="BBS II Leer auf Instagram"
              >
                <Instagram aria-hidden="true" />
              </a>
              <a
                href="https://www.tiktok.com/@bbs2leer"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-social-link tiktok-hover"
                aria-label="BBS II Leer auf TikTok"
              >
                <Tiktok aria-hidden="true" />
              </a>
              <a
                href="https://www.youtube.com/channel/UC-8-ii9B_PY1-3PJ1MdxPBA"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-social-link youtube-hover"
                aria-label="BBS II Leer auf YouTube"
              >
                <Youtube aria-hidden="true" />
              </a>
            </nav>

            <nav
              className="d-flex flex-wrap gap-3 align-items-center"
              aria-label="Schulische Online-Dienste"
            >
              <a
                href="https://bbs2.webuntis.com/WebUntis/?school=bbs2#/basic/login"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link custom-logo"
                title="Stundenplan über WebUntis aufrufen"
              >
                <img src={UntisLogo} alt="WebUntis Stundenplan" />
              </a>

              <a
                href="https://cloud.bbs2leer.de"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link custom-logo"
                title="Dateien in der Nextcloud verwalten"
              >
                <img src={NextcloudLogo} alt="Nextcloud Datei-Speicher" />
              </a>

              <a
                href="https://moodle.bbs2leer.de"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link custom-logo"
                title="Lernplattform Moodle öffnen"
              >
                <img src={MoodleLogo} alt="Moodle Lernplattform" />
              </a>

              <a
                href="https://www.netacad.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link custom-logo"
                title="Cisco Networking Academy"
              >
                <img src={CiscoLogo} alt="Cisco NetAcad" />
              </a>

              <a
                href="https://login.microsoftonline.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link custom-logo"
                title="Microsoft 365 Login"
              >
                <img src={MicrosoftLogo} alt="Microsoft 365" />
              </a>

              <a
                href="https://portal.bbs2leer.de"
                target="_blank"
                rel="noopener noreferrer"
                className="service-icon-link portal-link"
                aria-label="Zum BBS Schulportal"
              >
                <span className="portal-mini-text" aria-hidden="true">
                  BBS2portal
                </span>
                <span className="visually-hidden">BBS Portal</span>
              </a>
            </nav>
          </div>
        </Container>
      </div>

      <section
        className="landing-page border-bottom border-green-bbs"
        aria-labelledby="landing-hero-title"
      >
        <Container fluid>
          {/* 'align-items-center' sorgt dafür, dass die Row innerhalb der Landing-Page vertikal mittig sitzt */}
          <Row className="justify-content-start align-items-center h-100">
            <Col
              className="p-3 pt-0 ps-lg-5 text-center text-md-start text-white"
              xs={12}
              md={10}
              lg={6} // Etwas schmaler (6 statt 8), damit der Text links kompakt bricht und nicht in die Personen läuft
            >
              {/* 🔔 BADGE (wird gerendert, wenn ein Eintrag im JSON zeitlich matcht) */}
              {activeBadge && (
                <div className="mb-3">
                  <button
                    type="button"
                    className={`announcement-badge badge-bg-${activeBadge.bgType} border-0 rounded-pill px-3 py-2 text-danger fw-bold d-inline-flex align-items-center gap-2 shadow-sm`}
                    onClick={() => setActiveAnnouncement(activeBadge)}
                  >
                    <Megaphone className="fs-5 text-danger animated-megaphone" />
                    <span>{activeBadge.badgeText}</span>
                  </button>
                </div>
              )}

              <h1
                id="landing-hero-title"
                className="mb-4 landing-hero-title fw-300 text-uppercase-besser"
              >
                Mit der 2 <br />
                lernt man <br />
                <span className="d-block display-3 fw-bolder">BESSER!</span>
              </h1>

              {/* Neuer Untertext / Slogan */}
              <div className="landing-hero-subtitle fs-6 opacity-90">
                <p className="mb-1 fw-semibold">
                  Praxisnah. Zukunftsorientiert.
                </p>
                <p className="mb-0">Gemeinsam gestalten wir deine Zukunft</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 📜 OVERLAY AUSGELAGERT */}
      <AnnouncementModal
        announcement={activeAnnouncement}
        onClose={() => setActiveAnnouncement(null)}
      />
    </>
  );
};

export default LandingPage;
